/**
 * One MA brand's full row, fetched on demand — one request per row the
 * reviewer opens, and none on a revisit while it is still in the session LRU.
 *
 * Behind the session LRU sits the durable Firestore cache
 * (`external_brand_proposals`): a proposal opened within the last 7 days is
 * served from it without asking MA, an older one is re-fetched, and if MA
 * cannot be reached the cached copy is shown, marked stale. Policy:
 * proposalCachePolicy.ts.
 */
import { useEffect } from 'react';
import { fetchMaBrandDetail } from '../../services/microbrandAtlas';
import {
  firestoreProposalCache,
  saveProposalPlatformCheck,
} from '../../services/externalProposalCache';
import { loadProposalDetail, type PlatformCheck } from './proposalCachePolicy';
import {
  dropDetailEntry,
  getDetailEntry,
  setDetailEntry,
  touchDetailEntry,
  useSessionCache,
  type DetailEntry,
} from './sessionCache';

async function loadDetail(slug: string, force = false) {
  const existing = getDetailEntry(slug);
  if (existing?.status === 'loading') return;
  if (!force && existing?.status === 'ready') return;
  setDetailEntry(slug, { status: 'loading', detail: null, error: null });
  try {
    const loaded = await loadProposalDetail(
      slug,
      {
        fetchLive: fetchMaBrandDetail,
        store: firestoreProposalCache,
        // A cache that cannot be read or written (rules not deployed yet,
        // offline) must never break the review — it just isn't durable.
        onCacheError: (error) => console.warn('[proposals] proposal cache unavailable:', error),
      },
      { force },
    );
    setDetailEntry(slug, {
      status: 'ready',
      detail: loaded.detail,
      error: null,
      source: loaded.source,
      fetchedAt: loaded.fetchedAt,
      liveProblem: loaded.liveProblem,
      platformCheck: loaded.platformCheck,
    });
  } catch (error) {
    // Held only so the panel can show the message. `loadDetail` treats it as a
    // miss, so a failure is never served back as if it were the brand.
    setDetailEntry(slug, {
      status: 'error',
      detail: null,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export function retryMaBrandDetail(slug: string) {
  dropDetailEntry(slug);
  void loadDetail(slug);
}

/** The "Refresh" button: re-fetch from MA now, whatever the cache's age. */
export function refreshMaBrandDetail(slug: string) {
  void loadDetail(slug, true);
}

/**
 * Show a "Verify platform" result at once and persist it on the cache doc.
 * Resolves false when it could not be persisted (shown for this session only).
 */
export async function recordPlatformCheck(slug: string, check: PlatformCheck): Promise<boolean> {
  const entry = getDetailEntry(slug);
  if (entry) setDetailEntry(slug, { ...entry, platformCheck: check });
  try {
    await saveProposalPlatformCheck(slug, check);
    return true;
  } catch (error) {
    console.warn('[proposals] could not persist platform check:', error);
    return false;
  }
}

const IDLE: DetailEntry = { status: 'idle', detail: null, error: null };

export function useMaBrandDetail(slug: string | null): DetailEntry {
  // The entries live in a Map outside the store's state object; the version
  // counter is what tells this hook one of them changed.
  useSessionCache((s) => s.detailVersion);

  useEffect(() => {
    if (!slug) return;
    const entry = getDetailEntry(slug);
    if (entry?.status === 'ready') touchDetailEntry(slug);
    else if (!entry) void loadDetail(slug);
  }, [slug]);

  return (slug ? getDetailEntry(slug) : undefined) ?? IDLE;
}
